import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authClient from '../services/authClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authClient.isAuthenticated()) {
          // Verify token and get user data
          const verification = await authClient.verifyToken();
          if (verification.success) {
            const userData = authClient.getUserData();
            setUser(userData);
            setIsAuthenticated(true);

            // Load complete profile with astrology data
            try {
              const profileResponse = await authClient.getProfile();
              if (profileResponse.success) {
                setProfile(profileResponse.data);
              }
            } catch (error) {
              console.error('Failed to load profile:', error);
            }
          } else {
            // Token invalid, clear auth
            authClient.clearTokens();
            setIsAuthenticated(false);
            setUser(null);
            setProfile(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        authClient.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Signup function
  const signup = async (userData) => {
    try {
      setLoading(true);
      const response = await authClient.signup(userData);

      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);

        // Load profile data
        try {
          const profileResponse = await authClient.getProfile();
          if (profileResponse.success) {
            setProfile(profileResponse.data);
          }
        } catch (error) {
          console.error('Failed to load profile after signup:', error);
        }

        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Signup failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authClient.login(email, password);

      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);

        // Load profile data
        try {
          const profileResponse = await authClient.getProfile();
          if (profileResponse.success) {
            setProfile(profileResponse.data);
          }
        } catch (error) {
          console.error('Failed to load profile after login:', error);
        }

        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setProfile(null);
      setIsAuthenticated(false);
      // Redirect to home page after logout
      navigate('/');
    }
  };

  // Refresh profile data
  const refreshProfile = async () => {
    try {
      const response = await authClient.getProfile();
      if (response.success) {
        setProfile(response.data);
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to refresh profile' };
      }
    } catch (error) {
      console.error('Profile refresh error:', error);
      return { success: false, error: 'Failed to refresh profile' };
    }
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(userData);
    authClient.updateUserData(userData);
  };

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    signup,
    login,
    logout,
    refreshProfile,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
